'use client'

import * as React from 'react'
import { CheckCircle2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useKeywordsStore, useSettingsStore, useUIStore } from '@/lib/store'
import { localizeFeatureLabel } from '@/lib/feature-labels'
import { useI18n } from '@/lib/i18n'
import type { Listing } from '@/lib/types'

// MustHavesCard — design detail.jsx:84-111, wired to the real dealbreakers
// config (settings) and the keywords store for localized labels. Met keywords
// render as primary-container chips with a check; missed ones outlined with a
// strikethrough. Hidden entirely when no must-haves are configured.
export function MustHavesCard({ listing }: { listing: Pick<Listing, 'keywords'> }) {
  const { t } = useI18n()
  const mustHave = useSettingsStore((s) => s.dealbreakers.mustHave)
  const keywords = useKeywordsStore((s) => s.keywords)
  const uiLanguage = useUIStore((s) => s.uiLanguage)

  const defMap = React.useMemo(() => {
    const defs = keywords?.categories
      ? Object.values(keywords.categories).flatMap((cat) => cat.keywords)
      : []
    return new Map(defs.map((kw) => [kw.feature_key, kw]))
  }, [keywords])

  if (!mustHave.length) return null

  const label = (key: string) =>
    localizeFeatureLabel(key, uiLanguage, defMap.get(key))
  const listingKeywords = listing.keywords || []
  const met = mustHave.filter((k) => listingKeywords.includes(k))
  const missed = mustHave.filter((k) => !listingKeywords.includes(k))

  return (
    <div className="mt-3 rounded-shape-lg border border-outline-variant bg-surface px-[18px] pb-4 pt-[14px]">
      <div className="mb-[10px] flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-ink-muted">
          {t('listingDetail.mustHaves', 'Must-haves')}
        </span>
        <span
          className={cn(
            'font-mono text-[12px] font-bold',
            missed.length === 0 ? 'text-success' : 'text-ink-secondary'
          )}
        >
          {met.length}/{mustHave.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {met.map((k) => (
          <span
            key={k}
            className="inline-flex items-center gap-1 rounded-full bg-primary-container px-[11px] py-[5px] text-[12.5px] font-semibold text-on-primary-container"
          >
            <CheckCircle2 size={14} />
            {label(k)}
          </span>
        ))}
        {missed.map((k) => (
          <span
            key={k}
            className="inline-flex items-center gap-1 rounded-full border border-outline px-[11px] py-[5px] text-[12.5px] text-ink-muted line-through"
          >
            <X size={13} />
            {label(k)}
          </span>
        ))}
      </div>
    </div>
  )
}
